import { getContext, track } from '../core';

const trackedVideos = new WeakSet<HTMLElement>();

function getYouTubeTitle(iframe: HTMLIFrameElement): string {
  return iframe.title || iframe.getAttribute('aria-label') || 'YouTube Video';
}

function initYouTubeTracking(): void {
  // YouTube iframes send postMessage events when enablejsapi=1 is on the URL.
  window.addEventListener('message', (event: MessageEvent) => {
    let data: Record<string, unknown>;

    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    if (!data || data.event !== 'onStateChange') return;

    // YT.PlayerState.PLAYING = 1
    if (data.info !== 1) return;

    // Find the iframe that sent this message
    const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="youtube"]');
    for (const iframe of iframes) {
      if (!trackedVideos.has(iframe)) {
        trackedVideos.add(iframe);
        const ctx = getContext();
        track('video_start', {
          video_title: getYouTubeTitle(iframe),
          video_provider: 'youtube',
          page_name: ctx.page_title,
        });
        break;
      }
    }
  });
}

function initHTML5VideoTracking(): void {
  document.addEventListener(
    'play',
    (e) => {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      if (trackedVideos.has(video)) return;

      trackedVideos.add(video);
      const ctx = getContext();
      track('video_start', {
        video_title: video.title || video.getAttribute('aria-label') || 'HTML5 Video',
        video_provider: 'html5',
        page_name: ctx.page_title,
      });
    },
    true,
  );
}

function getVimeoTitle(iframe: HTMLIFrameElement): string {
  return iframe.title || iframe.getAttribute('aria-label') || 'Vimeo Video';
}

/**
 * Ensure Vimeo iframes have the `api=1` parameter so the postMessage API works.
 * Rewrites the src if needed — the iframe reloads with API enabled.
 */
function enableVimeoApi(): void {
  const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="vimeo.com"]');
  for (const iframe of iframes) {
    const src = iframe.src;
    if (!src) continue;
    try {
      const url = new URL(src);
      if (!url.searchParams.has('api')) {
        url.searchParams.set('api', '1');
        iframe.src = url.toString();
      }
    } catch {
      // malformed URL — skip
    }
  }
}

function initVimeoTracking(): void {
  // Ensure all Vimeo iframes have api=1 so postMessage works.
  enableVimeoApi();

  window.addEventListener('message', (event: MessageEvent) => {
    let data: Record<string, unknown>;

    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    if (!data) return;

    if (data.event === 'ready') {
      // When Vimeo iframe is ready, subscribe to the 'play' event.
      const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="vimeo"]');
      for (const iframe of iframes) {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ method: 'addEventListener', value: 'play' }),
          '*',
        );
      }
      return;
    }

    if (data.event === 'play') {
      const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe[src*="vimeo"]');
      for (const iframe of iframes) {
        if (!trackedVideos.has(iframe)) {
          trackedVideos.add(iframe);
          const ctx = getContext();
          track('video_start', {
            video_title: getVimeoTitle(iframe),
            video_provider: 'vimeo',
            page_name: ctx.page_title,
          });
          break;
        }
      }
    }
  });
}

export function initVideoTracking(): void {
  initYouTubeTracking();
  initVimeoTracking();
  initHTML5VideoTracking();
}
